import React, { useEffect, useState } from "react";
import {
    setAddElementModalVisibility,
    addEditCategory,
    addEditAptitude,
    setSelectedAptitudeForQuestion,
    setAddQuestionModalVisibility,
    deleteQuestion,
    setCurrentQuestion,
    aptitudeMcqVisibility,
    addEditQuestion,
    setQuestionsBank,
} from "../../vcompSlice";
import { useDispatch, useSelector } from "react-redux";
import { isAdmin, showErrorToast, showSuccessToast } from "../../../../app/utils/helpers";
import Langs, { Localize } from "../../../../app/lang/langs";
import ConfirmDialog from "../../../popups/ConfirmDialog";
import MobileAddQuestionModal from "./MobileAddQuestionModal";
import MobileNumberStepper from "./MobileNumberStepper";

// MUI
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { keyframes } from "@emotion/react";

/* ── Color tokens (existing palette) ───────────────────────────────── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255, 144, 21, 0.12)";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.35)";
const ORANGE_DARK = "#e07400";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const SURFACE = "rgba(255, 255, 255, 0.92)";
const SURFACE_TINT = "rgba(255, 144, 21, 0.03)";
const BORDER_SUBTLE = "rgba(0, 0, 0, 0.06)";
const RED = "#dc2626";

/* ── Keyframe animations ───────────────────────────────────────────── */

const fadeSlideUp = keyframes`
    0%   { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const softPulseGlow = keyframes`
    0%, 100% { box-shadow: 0 4px 16px rgba(255, 144, 21, 0.25); }
    50%      { box-shadow: 0 6px 24px rgba(255, 144, 21, 0.40); }
`;

const sectionReveal = (delay) => ({
    animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) ${delay}ms both`,
});

/* ── Shared styles ─────────────────────────────────────────────────── */

const glassCard = {
    background: "linear-gradient(168deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.93) 100%)",
    backdropFilter: "blur(12px) saturate(1.6)",
    WebkitBackdropFilter: "blur(12px) saturate(1.6)",
    borderRadius: "16px",
    border: `1px solid ${BORDER_SUBTLE}`,
    boxShadow: [
        "0 1px 3px rgba(0, 0, 0, 0.04)",
        "0 4px 12px rgba(0, 0, 0, 0.03)",
    ].join(", "),
    overflow: "hidden",
    transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
};

const sectionLabel = {
    fontSize: "0.7rem",
    fontWeight: 650,
    color: TEXT_MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    mb: 1.5,
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        bgcolor: "rgba(255, 255, 255, 0.9)",
        fontSize: "16px",
        minHeight: 52,
        transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
        "& fieldset": {
            borderColor: BORDER_SUBTLE,
            transition: "border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(255, 144, 21, 0.25)",
        },
        "&.Mui-focused fieldset": {
            borderColor: ORANGE,
            borderWidth: "1.5px",
            boxShadow: `0 0 0 3px rgba(255, 144, 21, 0.10)`,
        },
    },
    "& input, & textarea": { fontSize: "16px" },
    "& .MuiInputLabel-root": {
        fontSize: "15px",
        color: TEXT_SECONDARY,
        "&.Mui-focused": { color: ORANGE },
    },
};

const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: ORANGE,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: ORANGE,
    },
    "& .MuiSwitch-track": {
        transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1)",
    },
};

/* ── Transition ────────────────────────────────────────────────────── */

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddElementModal() {
    const dispatch = useDispatch();

    // === SELECTORS ===
    const props = useSelector((state) => state.vcomp.addElementModalVisibility);
    const userRoleId = useSelector((state) => state.login.userRoleId);
    const onCreateProject = useSelector((state) => state.vcomp.onCreateProject);
    const isMobile = useSelector((state) => state.settings.isMobile);
    const questionsBank = useSelector((state) => state.vcomp.questionsBank);
    const questionsFromStore = useSelector((state) => state.vcomp.selectedAptitudeForQuestion);

    // === STATE ===
    const [idElement, setIdElmement] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isSubcat, setIsSubCat] = useState(false);
    const [allowedOptions, setAllowedOptions] = useState(["Aptitude"]);
    const [selectedOption, setSelectedOption] = useState(allowedOptions[0]);
    const [idParent, setIdParent] = useState(null);
    const [pos, setPos] = useState(null);
    const [activeState, setActiveState] = useState(true);

    const [nom, setNom] = useState("");
    const [coef, setCoef] = useState(1);
    const [nomFlag, setNomFlag] = useState(false);
    const [description, setDescription] = useState("");

    const visibilityOptions = [
        { label: "Private", value: 1 },
        { label: "Public", value: 2 },
    ];
    const typesOptions = [
        { label: "Auto", value: 1 },
        { label: "MCQ", value: 2 },
    ];
    const [elVisibility, setElVisibility] = useState(visibilityOptions[0].value);

    const aspectOptions = [
        { label: Localize("Theoratical"), value: "theoretical" },
        { label: Localize("Practical"), value: "practical" },
    ];
    const [selectedAspect, setSelectedAspect] = useState(aspectOptions[0].value);
    const [type, setType] = useState(typesOptions[0].value);

    const [questions, setQuestions] = useState([]);
    const [questionDialogVisible, setQuestionDialogVisible] = useState(false);
    const [showQuestions, setShowQuestions] = useState(false);
    const [activeMcq, setActiveMcq] = useState(null);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);

    // === EFFECTS ===
    useEffect(() => {
        if (questionsFromStore) {
            setQuestions(questionsFromStore.questions);
        } else {
            setQuestions([]);
        }
    }, [questionsFromStore]);

    useEffect(() => {
        if (props) {
            setVisible(props.visibility);
            setIsSubCat(props.subcategory);
            if (!props.isEdit) {
                if (props.element) {
                    setIdParent(props.element.id);
                    setPos(props.element.children ? props.element.children.length : 0);
                    if (!props.subcategory) {
                        setAllowedOptions(["Aptitude", "Subcategory"]);
                    } else {
                        setAllowedOptions(["Aptitude"]);
                    }
                } else {
                    setPos(props.pos);
                    setAllowedOptions(["Category", "Aptitude"]);
                }
                if (props.isAdmin) {
                    setElVisibility(visibilityOptions[1].value);
                }
            } else {
                if (props.element.key.trim().toLowerCase().includes("cat")) {
                    setSelectedOption("Category");
                    setNom(props.element.label);
                    setIdElmement(props.element.id);
                    setElVisibility(props.element.visibility);
                    setCoef(props.element.coef);
                    setActiveState(props.element.active_state == 1 ? true : false);
                }
                if (props.element.key.trim().toLowerCase().includes("apt")) {
                    setSelectedOption("Aptitude");
                    setNom(props.element.label);
                    setDescription(props.element.description);
                    setIdElmement(props.element.id);
                    setElVisibility(props.element.visibility);
                    setCoef(props.element.coef);
                    setQuestions(props.element.questions);
                    setActiveMcq(props.element.active_mcq == 1);
                    setSelectedAspect(props.element.aspect);
                    setActiveState(props.element.active_state == 1 ? true : false);
                }
            }
        } else {
            setVisible(false);
            setIsSubCat(false);
            setIdParent(null);
            setPos(null);
            setNom("");
            setDescription("");
            setCoef(1);
            setAllowedOptions(["Aptitude"]);
            setNomFlag(false);
            setSelectedOption("Aptitude");
            setIdElmement(null);
            setElVisibility(visibilityOptions[0].value);
            setActiveState(true);
        }
    }, [props]);

    // === HANDLERS ===
    function handleClose() {
        dispatch(setAddElementModalVisibility(null));
        dispatch(setSelectedAptitudeForQuestion(null));
    }

    const setMcqVisibilityForAptitude = (e) => {
        if (onCreateProject && onCreateProject.id) {
            if (e === true) {
                dispatch(aptitudeMcqVisibility({ id_project: onCreateProject.id, activate: [idElement] }));
                setActiveMcq(true);
            }
            if (e === false) {
                dispatch(aptitudeMcqVisibility({ id_project: onCreateProject.id, deactivate: [idElement] }));
                setActiveMcq(false);
            }
        }
    };

    const handleElementSave = () => {
        if (nom.trim() == "" || nom == null) {
            setNomFlag(true);
        } else {
            if (!props.isEdit) {
                if (selectedOption === "Aptitude") {
                    dispatch(
                        addEditAptitude({
                            id_project: onCreateProject ? onCreateProject.id : null,
                            nom: nom,
                            coef: coef,
                            pos: pos,
                            description: description,
                            type: type == 1 ? "AUTO" : "MCQ",
                            id_category_default: idParent,
                            visibility: elVisibility,
                            aspect: selectedAspect,
                            active_state: activeState ? 1 : 0,
                        })
                    );
                }
                if (selectedOption === "Subcategory" || selectedOption === "Category") {
                    dispatch(
                        addEditCategory({
                            id_project: onCreateProject ? onCreateProject.id : null,
                            nom: nom,
                            coef: coef,
                            pos: pos,
                            id_parent_default: idParent,
                            visibility: elVisibility,
                            active_state: activeState ? 1 : 0,
                        })
                    );
                }
            } else {
                if (selectedOption === "Aptitude") {
                    dispatch(
                        addEditAptitude({
                            id_project: onCreateProject ? onCreateProject.id : null,
                            id: idElement,
                            nom: nom,
                            coef: coef,
                            pos: pos,
                            description: description,
                            type: type == 1 ? "AUTO" : "MCQ",
                            id_category_default: idParent,
                            visibility: elVisibility,
                            aspect: selectedAspect,
                            active_state: activeState ? 1 : 0,
                        })
                    );
                }
                if (selectedOption === "Subcategory" || selectedOption === "Category") {
                    dispatch(
                        addEditCategory({
                            id_project: onCreateProject ? onCreateProject.id : null,
                            id: idElement,
                            nom: nom,
                            coef: coef,
                            pos: pos,
                            id_parent_default: idParent,
                            visibility: elVisibility,
                            active_state: activeState ? 1 : 0,
                        })
                    );
                }
            }
            handleClose();
        }
    };

    const openQuestionDialog = (question) => {
        if (questions.length < 50) {
            dispatch(setCurrentQuestion(question));
            dispatch(setAddQuestionModalVisibility(true));
        } else {
            showErrorToast("Vous ne pouvez pas ajouter plus de 50 questions.");
        }
    };

    const handleRemoveFromAptitude = async (id) => {
        try {
            const question = questionsFromStore.questions.find((q) => q.id == id);
            if (!question) {
                showErrorToast("Question introuvable");
                return;
            }
            const updatedQuestion = { ...question, id_aptitude: null };
            await dispatch(addEditQuestion(updatedQuestion)).unwrap();
            const updatedQuestions = questionsFromStore.questions.filter((q) => q.id !== id);
            const updatedAptitude = { ...questionsFromStore, questions: updatedQuestions };
            dispatch(addEditAptitude(updatedAptitude));
            dispatch(setSelectedAptitudeForQuestion(updatedAptitude));
            dispatch(setQuestionsBank([...questionsBank, updatedQuestion]));
        } catch (error) {
            showErrorToast("Échec de la suppression de la question de l'aptitude");
            console.error(error);
        }
    };

    const handleDeleteFromProject = async (id) => {
        try {
            await dispatch(deleteQuestion({ id })).unwrap();
            const updatedQuestions = questionsFromStore.questions.filter((q) => q.id !== id);
            const updatedAptitude = { ...questionsFromStore, questions: updatedQuestions };
            dispatch(addEditAptitude(updatedAptitude));
            dispatch(setSelectedAptitudeForQuestion(updatedAptitude));
        } catch (error) {
            showErrorToast("Échec de la suppression de la question du projet");
            console.error(error);
        }
    };

    const removeQuestion = (id) => {
        setSelectedQuestionId(id);
        setDialogVisible(true);
    };

    const isFieldDisabled = elVisibility === 2 && userRoleId !== 1;

    // === RENDER ===
    return (
        <>
            <Dialog
                fullScreen
                open={visible || false}
                onClose={handleClose}
                TransitionComponent={Transition}
                transitionDuration={{ enter: 380, exit: 280 }}
                sx={{ zIndex: 1500 }}
            >
                {/* ── Glassmorphic AppBar ──────────────────────────────── */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: "rgba(255, 255, 255, 0.85)",
                        backdropFilter: "blur(16px) saturate(1.8)",
                        WebkitBackdropFilter: "blur(16px) saturate(1.8)",
                        borderBottom: `1px solid ${BORDER_SUBTLE}`,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: `linear-gradient(90deg, ${ORANGE}, #FFC100, ${ORANGE})`,
                            backgroundSize: "200% auto",
                            animation: `${shimmer} 3s ease-in-out infinite`,
                            opacity: 0.6,
                        },
                    }}
                >
                    <Toolbar sx={{ justifyContent: "space-between", minHeight: 56 }}>
                        <IconButton
                            edge="start"
                            onClick={handleClose}
                            sx={{
                                color: TEXT_SECONDARY,
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                bgcolor: "rgba(0, 0, 0, 0.03)",
                                transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:active": {
                                    transform: "scale(0.92)",
                                    bgcolor: "rgba(0, 0, 0, 0.06)",
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 650,
                                color: TEXT_PRIMARY,
                                textAlign: "center",
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                letterSpacing: "-0.02em",
                                fontSize: "1.05rem",
                                ...sectionReveal(80),
                            }}
                        >
                            {props && props.isEdit ? (
                                <Langs str="EDIT_ELEMENT" />
                            ) : (
                                <Langs str="ADD_ELEMENT" />
                            )}
                        </Typography>
                        <Box sx={{ width: 48 }} />
                    </Toolbar>
                </AppBar>

                {/* ── Scrollable body ─────────────────────────────────── */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        WebkitOverflowScrolling: "touch",
                        bgcolor: "#f8f9fb",
                        background: `linear-gradient(180deg, rgba(255,144,21,0.02) 0%, #f8f9fb 120px)`,
                        px: 2,
                        py: 3,
                        pb: 14,
                        mt: 0,
                        scrollBehavior: "smooth",
                        /* Custom scrollbar */
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: "rgba(0, 0, 0, 0.10)",
                            borderRadius: 3,
                        },
                    }}
                >
                    {/* ── Section: Element Type (create mode only) ──── */}
                    {props && !props.isEdit && (
                        <Box sx={{ mb: 3, ...sectionReveal(100) }}>
                            <Typography sx={sectionLabel}>Type d'élément</Typography>
                            <Box sx={glassCard}>
                                <Box sx={{ p: 2 }}>
                                    <FormControl fullWidth>
                                        <InputLabel sx={{ fontSize: "15px" }}>
                                            <Langs str="Element Type" />
                                        </InputLabel>
                                        <Select
                                            value={selectedOption}
                                            onChange={(e) => setSelectedOption(e.target.value)}
                                            label="Element Type"
                                            MenuProps={{ sx: { zIndex: 1600 } }}
                                            sx={{
                                                ...inputSx["& .MuiOutlinedInput-root"],
                                                fontSize: "16px",
                                                borderRadius: "12px",
                                                minHeight: 52,
                                                bgcolor: "rgba(255, 255, 255, 0.8)",
                                                "& fieldset": { borderColor: BORDER_SUBTLE },
                                                "&:hover fieldset": { borderColor: "rgba(255, 144, 21, 0.25)" },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: ORANGE,
                                                    borderWidth: "1.5px",
                                                },
                                            }}
                                        >
                                            {allowedOptions.map((opt) => (
                                                <MenuItem key={opt} value={opt} sx={{ minHeight: 48, borderRadius: "8px", mx: 0.5 }}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* ── Section: Details ─────────────────────────── */}
                    <Box sx={{ mb: 3, ...sectionReveal(180) }}>
                        <Typography sx={sectionLabel}>Détails</Typography>
                        <Box sx={{ ...glassCard, p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            {/* Element Name */}
                            <TextField
                                fullWidth
                                label={`${selectedOption} nom`}
                                value={nom}
                                onChange={(e) => {
                                    setNom(e.target.value);
                                    if (e.target.value.trim() !== "") setNomFlag(false);
                                }}
                                error={nomFlag}
                                helperText={nomFlag ? "Ce champ est requis" : ""}
                                disabled={isFieldDisabled}
                                sx={inputSx}
                            />

                            {/* Description (Aptitude only) */}
                            {selectedOption === "Aptitude" && (
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={isFieldDisabled}
                                    multiline
                                    minRows={2}
                                    sx={inputSx}
                                />
                            )}

                            {/* Aspect (Aptitude only) */}
                            {selectedOption === "Aptitude" && (
                                <FormControl fullWidth>
                                    <InputLabel sx={{ fontSize: "15px" }}>
                                        <Langs str="Aspect" />
                                    </InputLabel>
                                    <Select
                                        value={selectedAspect}
                                        onChange={(e) => setSelectedAspect(e.target.value)}
                                        label="Aspect"
                                        disabled={isFieldDisabled}
                                        MenuProps={{ sx: { zIndex: 1600 } }}
                                        sx={{
                                            fontSize: "16px",
                                            borderRadius: "12px",
                                            minHeight: 52,
                                            bgcolor: "rgba(255, 255, 255, 0.8)",
                                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            "& fieldset": { borderColor: BORDER_SUBTLE },
                                            "&:hover fieldset": { borderColor: "rgba(255, 144, 21, 0.25)" },
                                            "&.Mui-focused fieldset": {
                                                borderColor: ORANGE,
                                                borderWidth: "1.5px",
                                            },
                                        }}
                                    >
                                        {aspectOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value} sx={{ minHeight: 48, borderRadius: "8px", mx: 0.5 }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    </Box>

                    {/* ── Section: Coefficient ────────────────────── */}
                    <Box sx={{ mb: 3, ...sectionReveal(260) }}>
                        <Typography sx={sectionLabel}>Coefficient</Typography>
                        <Box sx={{ ...glassCard, p: 2.5 }}>
                            <MobileNumberStepper
                                label={null}
                                value={coef}
                                onChange={(e) => setCoef(e.value)}
                                min={1}
                                max={100}
                                disabled={isFieldDisabled}
                            />
                        </Box>
                    </Box>

                    {/* ── Section: Toggles ────────────────────────── */}
                    {props && onCreateProject && onCreateProject.id && (
                        <Box sx={{ mb: 3, ...sectionReveal(340) }}>
                            <Typography sx={sectionLabel}>Options</Typography>
                            <Box sx={{ ...glassCard, p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {/* Active State */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={activeState}
                                            onChange={(e) => setActiveState(e.target.checked)}
                                            sx={switchSx}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: TEXT_SECONDARY, fontSize: "0.9rem" }}>
                                            Actif
                                        </Typography>
                                    }
                                    sx={{ ml: 0, py: 0.5 }}
                                />

                                {/* Enable Questions (Aptitude, edit mode, project in DB) */}
                                {selectedOption === "Aptitude" &&
                                    props &&
                                    props.isEdit === 1 && (
                                        <>
                                            <Divider sx={{ my: 0.5, borderColor: BORDER_SUBTLE }} />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={activeMcq || false}
                                                        onChange={(e) => setMcqVisibilityForAptitude(e.target.checked)}
                                                        sx={switchSx}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: TEXT_SECONDARY, fontSize: "0.9rem" }}>
                                                        Activer les Questions
                                                    </Typography>
                                                }
                                                sx={{ ml: 0, py: 0.5 }}
                                            />
                                        </>
                                    )}
                            </Box>
                        </Box>
                    )}

                    {/* ── Section: Questions list ─────────────────── */}
                    {selectedOption === "Aptitude" &&
                        props &&
                        props.isEdit === 1 &&
                        onCreateProject &&
                        onCreateProject.id && (
                            <Box sx={{ mb: 3, ...sectionReveal(420) }}>
                                <Typography sx={sectionLabel}>Questions</Typography>
                                <Box sx={glassCard}>
                                    {/* Questions header */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            px: 2.5,
                                            py: 2,
                                            borderBottom: `1px solid ${BORDER_SUBTLE}`,
                                            background: SURFACE_TINT,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 650,
                                                    color: TEXT_PRIMARY,
                                                    fontSize: "0.9rem",
                                                    letterSpacing: "-0.01em",
                                                }}
                                            >
                                                Questions
                                            </Typography>
                                            {questions && questions.length > 0 && (
                                                <Chip
                                                    label={questions.length}
                                                    size="small"
                                                    sx={{
                                                        height: 24,
                                                        fontSize: "0.75rem",
                                                        bgcolor: ORANGE_LIGHT,
                                                        color: ORANGE,
                                                        fontWeight: 700,
                                                        borderRadius: "8px",
                                                        "& .MuiChip-label": { px: 1 },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <IconButton
                                            onClick={() => openQuestionDialog()}
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                color: "#fff",
                                                background: `linear-gradient(135deg, ${ORANGE} 0%, #FFA940 100%)`,
                                                borderRadius: "12px",
                                                boxShadow: `0 2px 8px rgba(255, 144, 21, 0.25)`,
                                                transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                "&:active": {
                                                    transform: "scale(0.92)",
                                                    boxShadow: `0 1px 4px rgba(255, 144, 21, 0.20)`,
                                                },
                                            }}
                                        >
                                            <AddIcon sx={{ fontSize: 22 }} />
                                        </IconButton>
                                    </Box>

                                    {/* Questions items */}
                                    {questions && questions.length > 0 ? (
                                        <List
                                            disablePadding
                                            sx={{
                                                maxHeight: 320,
                                                overflow: "auto",
                                                "&::-webkit-scrollbar": { width: 4 },
                                                "&::-webkit-scrollbar-thumb": {
                                                    background: "rgba(0, 0, 0, 0.08)",
                                                    borderRadius: 2,
                                                },
                                            }}
                                        >
                                            {questions.map((item, index) => (
                                                <React.Fragment key={item.id || index}>
                                                    <ListItem
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2.5,
                                                            pr: 12,
                                                            minHeight: 60,
                                                            transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                            "&:active": {
                                                                bgcolor: "rgba(255, 144, 21, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={item.question}
                                                            primaryTypographyProps={{
                                                                variant: "body2",
                                                                sx: {
                                                                    color: TEXT_PRIMARY,
                                                                    lineHeight: 1.6,
                                                                    fontSize: "0.875rem",
                                                                    fontWeight: 400,
                                                                },
                                                            }}
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openQuestionDialog(item)}
                                                                sx={{
                                                                    width: 38,
                                                                    height: 38,
                                                                    color: TEXT_SECONDARY,
                                                                    borderRadius: "10px",
                                                                    mr: 0.5,
                                                                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                    "&:active": {
                                                                        bgcolor: "rgba(0, 0, 0, 0.06)",
                                                                        transform: "scale(0.92)",
                                                                    },
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 19 }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeQuestion(item.id)}
                                                                sx={{
                                                                    width: 38,
                                                                    height: 38,
                                                                    color: RED,
                                                                    borderRadius: "10px",
                                                                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                    "&:active": {
                                                                        bgcolor: "rgba(220, 38, 38, 0.06)",
                                                                        transform: "scale(0.92)",
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 19 }} />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                    {index < questions.length - 1 && (
                                                        <Divider sx={{ mx: 2.5, borderColor: BORDER_SUBTLE }} />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{ py: 5, textAlign: "center" }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: TEXT_MUTED,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                Aucune question ajoutée
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <MobileAddQuestionModal />
                            </Box>
                        )}
                </Box>

                {/* ── Sticky bottom save bar ───────────────────────── */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "rgba(255, 255, 255, 0.80)",
                        backdropFilter: "blur(20px) saturate(1.8)",
                        WebkitBackdropFilter: "blur(20px) saturate(1.8)",
                        borderTop: `1px solid ${BORDER_SUBTLE}`,
                        px: 2.5,
                        py: 2,
                        pb: "calc(16px + env(safe-area-inset-bottom))",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        onClick={handleElementSave}
                        sx={{
                            background: `linear-gradient(135deg, ${ORANGE} 0%, #FFA940 100%)`,
                            color: "#fff",
                            borderRadius: "14px",
                            minHeight: 52,
                            fontWeight: 650,
                            textTransform: "none",
                            fontSize: "1rem",
                            letterSpacing: "0.01em",
                            position: "relative",
                            overflow: "hidden",
                            boxShadow: `0 4px 16px rgba(255, 144, 21, 0.30)`,
                            animation: `${softPulseGlow} 3s ease-in-out infinite`,
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%)",
                                borderRadius: "inherit",
                                pointerEvents: "none",
                            },
                            "&:hover": {
                                background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 100%)`,
                                boxShadow: `0 6px 24px rgba(255, 144, 21, 0.40)`,
                                transform: "translateY(-1px)",
                            },
                            "&:active": {
                                transform: "scale(0.97) translateY(0px)",
                                boxShadow: `0 2px 8px rgba(255, 144, 21, 0.25)`,
                                transition: "all 100ms ease",
                            },
                        }}
                    >
                        <Langs str="SAVE" />
                    </Button>
                </Box>
            </Dialog>

            {/* ── Delete confirmation dialog (z-index 1600) ────────── */}
            {/* Two different deletes, neither a dismissal: closing the dialog does nothing. */}
            <ConfirmDialog
                open={dialogVisible}
                danger
                title={<Langs str="DELETE_QUESTION" />}
                message={<Langs str="DELETE_QUESTION_SCOPE_MSG" />}
                cancelLabel={<Langs str="REMOVE_FROM_APTITUDE" />}
                confirmLabel={<Langs str="DELETE_FROM_PROJECT" />}
                onCancel={() => {
                    handleRemoveFromAptitude(selectedQuestionId);
                    setDialogVisible(false);
                }}
                onConfirm={() => {
                    handleDeleteFromProject(selectedQuestionId);
                    setDialogVisible(false);
                }}
                onClose={() => setDialogVisible(false)}
            />
        </>
    );
}
